import { formatDate, loc, t, type TranslationKey } from '../i18n';
import type { Achievement, MediaItem, News, PlayerShort, Race, Roster } from '../types';
import { h, link, pad, plain, svg, youtubeId } from './dom';
import { icons } from './icons';
import { openLightbox } from './overlay';

export function categoryLabel(cat: string): string {
  return t(`news.cat.${cat}` as TranslationKey);
}

export function raceLabel(race: string): string {
  return t(`race.${race}` as TranslationKey);
}

export function roleLabel(role: string): string {
  return t(`role.${role}` as TranslationKey);
}

export function raceBadge(race: Race, withLabel = false): HTMLElement {
  return h(
    'span',
    { class: `race race-${race}`, attrs: { title: raceLabel(race) } },
    h('span', 'race-glyph', race.charAt(0).toUpperCase()),
    withLabel ? h('span', 'race-name', raceLabel(race)) : null,
  );
}

export function rosterRange(r: Pick<Roster, 'mmr_min' | 'mmr_max'>): string {
  if (r.mmr_min != null && r.mmr_max != null) return t('roster.mmrRange', { min: r.mmr_min, max: r.mmr_max });
  if (r.mmr_min != null) return t('roster.mmrFrom', { min: r.mmr_min });
  if (r.mmr_max != null) return t('roster.mmrTo', { max: r.mmr_max });
  return '—';
}

/** Fallback cover: emblem on a grid. */
function coverPlaceholder(): HTMLElement {
  return h('div', 'cover-ph', h('img', { attrs: { src: '/brand/emblem.jpg', alt: '', loading: 'lazy' } }));
}

export function newsCard(n: News, index?: number): HTMLElement {
  const href = `/news?id=${n.id}`;
  const title = loc(n, 'title');
  return link(
    href,
    'news-card',
    h(
      'div',
      'news-cover',
      n.cover_url ? h('img', { attrs: { src: n.cover_url, alt: '', loading: 'lazy' } }) : coverPlaceholder(),
      h('span', { class: `tag tag-${n.category}`, text: categoryLabel(n.category) }),
    ),
    h(
      'div',
      'news-body',
      h(
        'div',
        'news-meta mono',
        index !== undefined ? h('span', null, `#${pad(index, 3)}`) : null,
        h('time', { attrs: { datetime: n.published_at }, text: formatDate(n.published_at) }),
        n.is_published === false ? h('span', { class: 'accent', text: t('admin.draft') }) : null,
      ),
      h('h3', { text: title }),
      h('p', { text: plain(loc(n, 'summary') || loc(n, 'body'), 180) }),
      h('span', 'news-more', t('common.readMore'), svg(icons.arrowRight)),
    ),
  );
}

export function isTopPlace(place: string): 1 | 2 | 3 | null {
  const m = /^\s*(\d+)/.exec(place);
  const n = m ? Number(m[1]) : NaN;
  return n === 1 || n === 2 || n === 3 ? n : null;
}

export function placeBadge(place: string): HTMLElement {
  const top = isTopPlace(place);
  return h('span', { class: `place${top ? ` place-${top}` : ''}`, text: place });
}

export function achievementRow(a: Achievement): HTMLElement {
  const note = loc(a, 'note');
  return h(
    'article',
    'tl-item',
    h('div', 'tl-date mono', h('time', { attrs: { datetime: a.event_date }, text: formatDate(a.event_date) })),
    h('span', 'tl-node'),
    h(
      'div',
      'tl-card',
      h(
        'div',
        'tl-main',
        placeBadge(a.place),
        h(
          'div',
          'tl-text',
          h('h3', { text: loc(a, 'title') }),
          note ? h('p', { text: note }) : null,
          h(
            'dl',
            'tl-facts',
            h('div', null, h('dt', { text: t('ach.mmr') }), h('dd', { text: a.mmr || '—' })),
            h('div', null, h('dt', { text: t('ach.place') }), h('dd', { text: a.place })),
          ),
        ),
      ),
      h(
        'div',
        'tl-links',
        a.source_url ? link(a.source_url, 'btn btn-ghost btn-sm', svg(icons.challonge), t('ach.source')) : null,
        a.vod_url ? link(a.vod_url, 'btn btn-ghost btn-sm', svg(icons.play), t('ach.vod')) : null,
      ),
    ),
  );
}

export function mediaPreview(m: Pick<MediaItem, 'preview_url' | 'url' | 'media_type'>): string | null {
  if (m.preview_url) return m.preview_url;
  const yt = youtubeId(m.url);
  if (yt) return `https://i.ytimg.com/vi/${yt}/hqdefault.jpg`;
  if (m.media_type === 'screenshot' || m.media_type === 'photo') return m.url;
  return null;
}

export function mediaCard(m: MediaItem): HTMLElement {
  const preview = mediaPreview(m);
  const isImage = m.media_type === 'screenshot' || m.media_type === 'photo';
  const title = loc(m, 'title');
  const inner = [
    h(
      'div',
      'media-thumb',
      preview ? h('img', { attrs: { src: preview, alt: '', loading: 'lazy' } }) : coverPlaceholder(),
      h('span', 'media-play', svg(isImage ? icons.image : icons.play)),
      h('span', { class: 'tag', text: t(`media.type.${m.media_type}` as TranslationKey) }),
    ),
    h(
      'div',
      'media-info',
      h('h3', { text: title }),
      h('time', { class: 'mono', attrs: { datetime: m.published_at }, text: formatDate(m.published_at) }),
    ),
  ];
  if (isImage) {
    return h('button', { class: 'media-card', attrs: { type: 'button' }, on: { click: () => openLightbox(m.url, title) } }, ...inner);
  }
  return link(m.url, 'media-card', ...inner);
}

export function playerChip(p: PlayerShort): HTMLElement {
  return link(
    `/player?id=${p.id}`,
    `player-chip role-${p.role}`,
    raceBadge(p.race),
    h('span', 'player-nick', p.nickname),
    p.role !== 'member' ? h('span', { class: 'player-role mono', text: roleLabel(p.role) }) : null,
    p.mmr ? h('span', 'player-mmr mono', String(p.mmr)) : null,
  );
}
